import { json, type ActionFunctionArgs, type LinksFunction, type MetaFunction } from '@remix-run/node';
import { Landing } from '~/deck2/landing/Landing';
import { ROLES, SHAPES, USES } from '~/deck2/case/config';
import type { StartActionData } from '~/deck2/landing/StartFlow';
import { clientAddress } from '~/lib/client-address.server';
import { captureOnServer } from '~/lib/posthog.server';
import { appendWaitlistSignup, isOnWaitlist } from '~/lib/waitlist.server';

export const meta: MetaFunction = () => [
  { title: 'Deck · Your next case study gets you hired' },
  { name: 'description', content: 'Pick what it is about, bring your work, and Deck turns it into a case study you can present.' },
];

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Libre+Baskerville&family=Newsreader:opsz,wght@6..72,400&display=swap' },
  { rel: 'preload', href: '/landing/fonts/ABCArealSuperfamilyVariable.ttf', as: 'font', type: 'font/ttf', crossOrigin: 'anonymous' },
];

/**
 * "Start your case study" before Deck is out: the answers and the email join the same signup file as the
 * waitlist, with the answers in its source column, so one list holds everyone waiting.
 */
/** A person signs up once; a script would sign up thousands of times. Each address gets a few an hour. */
const SIGNUPS_PER_HOUR = 5;
const signupsFrom = new Map<string, number[]>();

function allowedFrom(address: string): boolean {
  const now = Date.now();
  const recent = (signupsFrom.get(address) ?? []).filter((at) => now - at < 60 * 60 * 1000);
  if (recent.length >= SIGNUPS_PER_HOUR) return false;
  if (signupsFrom.size > 5000) signupsFrom.clear();
  signupsFrom.set(address, [...recent, now]);
  return true;
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData();

  // A filled honeypot is a bot: report success and write nothing.
  if (String(form.get('cs_trap') ?? '').trim() !== '') return json<StartActionData>({ ok: true });

  const email = String(form.get('email') ?? '').trim().toLowerCase();
  if (!email || email.length > 320 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return json<StartActionData>({ ok: false, error: 'That email does not look right.' }, { status: 400 });
  }

  if (!allowedFrom(clientAddress(request))) {
    return json<StartActionData>(
      { ok: false, error: "We've had a lot of signups from your network. Please try again in a little while." },
      { status: 429 },
    );
  }

  // Already on the list: they see the same Monday screen, and nothing is added twice.
  if (await isOnWaitlist(email)) return json<StartActionData>({ ok: true });

  // Picked answers are kept by id. The company is the one thing typed, so it is cut short and kept to one
  // plain line of letters, digits and simple punctuation.
  const shape = String(form.get('shape') ?? '');
  const use = String(form.get('use') ?? '');
  const role = String(form.get('role') ?? '');
  const about = SHAPES.some((s) => s.id === shape) ? shape : 'not picked';
  const where = USES.some((u) => u.id === use) ? use : 'not picked';
  const what = ROLES.some((r) => r.id === role) ? role : 'not picked';
  const company =
    String(form.get('company') ?? '')
      .replace(/[^\p{L}\p{N} .&'()/-]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80) || 'not given';

  try {
    await appendWaitlistSignup(email, `case study: ${about}, ${where}, ${what} at ${company}`);
  } catch (e) {
    console.error('Case study signup failed:', e instanceof Error ? e.message : e);
    return json<StartActionData>({ ok: false, error: 'Something went wrong. Please try again.' }, { status: 500 });
  }
  // Everyone waiting is also a person in PostHog, with their answers, beside the file.
  await captureOnServer('case_study_signup', email, {
    about,
    use: where,
    role: what,
    company,
    host: new URL(request.url).host,
    $set: { email, company, role: what, case_study_about: about, case_study_use: where, signed_up_from: 'case study landing' },
    $set_once: { first_signed_up_at: new Date().toISOString() },
  });
  return json<StartActionData>({ ok: true });
}

export default function CaseStudyLanding() {
  return <Landing />;
}
